import { useState, useEffect, useCallback } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import axios from "axios";
import {
  Activity, Shield, AlertTriangle, AlertCircle, Info, CheckCircle2,
  Trash2, Sparkles, RefreshCw, Loader2, Eye, Search, Filter,
  Wifi, Rss, Server, Zap, Monitor, XCircle, Clock,
  Bug, Code, MessageSquareWarning
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ModuleDescription } from "@/components/ModuleDescription";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const severityConfig = {
  critical: { icon: XCircle, color: "text-red-500", bg: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400", border: "border-red-200 dark:border-red-900" },
  high: { icon: AlertTriangle, color: "text-amber-500", bg: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400", border: "border-amber-200 dark:border-amber-900" },
  medium: { icon: AlertCircle, color: "text-blue-500", bg: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400", border: "border-blue-200 dark:border-blue-900" },
  low: { icon: Info, color: "text-slate-500", bg: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400", border: "border-slate-200 dark:border-slate-800" },
  info: { icon: Info, color: "text-slate-400", bg: "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-500", border: "border-slate-200 dark:border-slate-800" },
};

const typeConfig = {
  wp_api: { icon: Wifi, label: "WordPress API" },
  rss_fetch: { icon: Rss, label: "RSS Feed" },
  ftp_connect: { icon: Server, label: "FTP" },
  ai_generation: { icon: Zap, label: "AI Generation" },
  system: { icon: Monitor, label: "System" },
  backend_exception: { icon: Code, label: "Backend Error" },
  frontend_error: { icon: Bug, label: "Frontend Error" },
  user_report: { icon: MessageSquareWarning, label: "Zgłoszenie usera" },
  domain_expiring: { icon: Clock, label: "Domena wygasa" },
  domain_in_quarantine: { icon: AlertTriangle, label: "Domena w kwarantannie" },
  domain_lost: { icon: XCircle, label: "Domena UTRACONA" },
};

const statusConfig = {
  new: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  acknowledged: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  resolved: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
  ignored: "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-500",
};

export default function AiMonitor() {
  const { t } = useLanguage();
  const [alerts, setAlerts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [filterType, setFilterType] = useState("all");
  const [filterSeverity, setFilterSeverity] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [analyzingId, setAnalyzingId] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkProcessing, setBulkProcessing] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterType !== "all") params.set("error_type", filterType);
      if (filterSeverity !== "all") params.set("severity", filterSeverity);
      if (filterStatus !== "all") params.set("status", filterStatus);

      const [alertsRes, statsRes] = await Promise.all([
        axios.get(`${API}/monitor/alerts?${params.toString()}`),
        axios.get(`${API}/monitor/stats`),
      ]);
      setAlerts(alertsRes.data);
      setStats(statsRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [filterType, filterSeverity, filterStatus]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleScan = async () => {
    setScanning(true);
    try {
      const res = await axios.post(`${API}/monitor/scan`);
      toast.success(`${t("monitor.scanComplete")}: ${res.data.errors_found} ${t("monitor.errorsFound")}, ${res.data.new_alerts_saved} ${t("monitor.newAlerts")}`);
      fetchData();
    } catch (err) { toast.error(t("common.error")); }
    finally { setScanning(false); }
  };

  const handleAnalyze = async (alertId) => {
    setAnalyzingId(alertId);
    try {
      const res = await axios.post(`${API}/monitor/alerts/${alertId}/analyze`, { ai_model: "gemini" });
      toast.success(t("monitor.analysisComplete"));
      fetchData();
      // Odswiez wybrany alert
      if (selectedAlert?.id === alertId) {
        const updated = alerts.find(a => a.id === alertId);
        if (updated) setSelectedAlert({ ...updated, ai_analysis: res.data });
      }
    } catch (err) { toast.error(t("common.error")); }
    finally { setAnalyzingId(null); }
  };

  const handleStatusChange = async (alertId, newStatus) => {
    try {
      await axios.patch(`${API}/monitor/alerts/${alertId}`, { status: newStatus });
      toast.success(t("common.success"));
      fetchData();
    } catch (err) { toast.error(t("common.error")); }
  };

  const toggleSelect = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedIds(next);
  };

  const selectAllVisible = () => {
    const visibleNew = alerts.filter(a => a.status === "new");
    setSelectedIds(new Set(visibleNew.map(a => a.id)));
  };

  const clearSelection = () => setSelectedIds(new Set());

  const handleBulkResolve = async (statusTarget = "resolved") => {
    if (selectedIds.size === 0) {
      toast.error("Brak zaznaczonych alertów");
      return;
    }
    if (!window.confirm(`${statusTarget === "resolved" ? "Oznaczyć jako rozwiązane" : statusTarget}: ${selectedIds.size} alertów?`)) return;
    setBulkProcessing(true);
    try {
      const res = await axios.patch(`${API}/monitor/alerts/bulk`, {
        ids: Array.from(selectedIds),
        new_status: statusTarget,
        notes: `Bulk action via UI ${new Date().toISOString().slice(0,10)}`,
      });
      toast.success(`Zmieniono ${res.data.modified} z ${res.data.matched} alertów`);
      clearSelection();
      fetchData();
    } catch (err) {
      toast.error("Bulk update nieudany: " + (err.response?.data?.detail || err.message));
    } finally {
      setBulkProcessing(false);
    }
  };

  const handleBulkByFilter = async () => {
    const olderHours = prompt("Resolve alertów starszych niż ile godzin? (1-720, default 168=7 dni)", "168");
    if (!olderHours) return;
    const hours = parseInt(olderHours, 10);
    if (isNaN(hours) || hours < 1 || hours > 720) {
      toast.error("Niepoprawna wartość");
      return;
    }
    const filter = {
      older_than_hours: hours,
      new_status: "resolved",
      notes: `Bulk: alerty >${hours}h`,
    };
    if (filterType !== "all") filter.error_type = filterType;
    if (filterSeverity !== "all") filter.severity = filterSeverity;
    if (!window.confirm(`Resolve wszystkich alertów starszych niż ${hours}h z aktualnymi filtrami?`)) return;
    setBulkProcessing(true);
    try {
      const res = await axios.patch(`${API}/monitor/alerts/bulk`, filter);
      toast.success(`Zmieniono ${res.data.modified} z ${res.data.matched} alertów`);
      fetchData();
    } catch (err) {
      toast.error("Bulk update nieudany: " + (err.response?.data?.detail || err.message));
    } finally {
      setBulkProcessing(false);
    }
  };

  const handleDelete = async (alertId) => {
    try {
      await axios.delete(`${API}/monitor/alerts/${alertId}`);
      toast.success(t("common.success"));
      if (selectedAlert?.id === alertId) setSelectedAlert(null);
      fetchData();
    } catch (err) { toast.error(t("common.error")); }
  };

  return (
    <div data-testid="monitor-page" className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-heading text-4xl font-bold tracking-tight text-foreground">
            {t("monitor.title")}
          </h1>
          <p className="text-sm text-muted-foreground mt-1 font-body">{t("monitor.subtitle")}</p>
        </div>
      <ModuleDescription title="Jak dziala AI Monitor?">
        <p><strong>AI Monitor</strong> wykrywa bledy w systemie i automatycznie je analizuje przez AI.</p>
        <p><strong>Typy alertow:</strong> WordPress API (bledy polaczenia ze stronami), RSS Feed (problemy z importem), AI Generation (bledy generowania tresci), Publish Error (nieudane publikacje), CTR Effect (efekty optymalizacji tytulow po 7 dniach).</p>
        <p><strong>Waznosc:</strong> Krytyczne (czerwone) = natychmiastowa uwaga | Wysokie (pomaranczowe) = wazne | Srednie (niebieskie) = do sprawdzenia | Niskie (szare) = informacyjne.</p>
        <p><strong>"Skanuj system"</strong> = uruchom pelny skan wszystkich stron i serwisow. Automatycznie wykrywa awarie stron, bledy publikacji i efekty optymalizacji CTR.</p>
        <p><strong>Analiza AI</strong> = kliknij na alert i wybierz "Analizuj AI" aby Gemini zaproponowal rozwiazanie.</p>
      </ModuleDescription>

        <Button data-testid="scan-btn" onClick={handleScan} disabled={scanning} className="gap-2 rounded-lg">
          {scanning ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" strokeWidth={1.5} />}
          {scanning ? t("monitor.scanning") : t("monitor.runScan")}
        </Button>
      </div>

      {/* Statystyki */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { label: t("monitor.totalAlerts"), value: stats.total, icon: Activity, color: "text-slate-500" },
            { label: t("monitor.newAlerts"), value: stats.new, icon: AlertCircle, color: "text-red-500" },
            { label: t("monitor.critical"), value: stats.critical, icon: XCircle, color: "text-red-600" },
            { label: t("monitor.highPriority"), value: stats.high, icon: AlertTriangle, color: "text-amber-500" },
            { label: t("monitor.resolved"), value: stats.resolved, icon: CheckCircle2, color: "text-emerald-500" },
          ].map((s, i) => (
            <Card key={i} data-testid={`monitor-stat-${i}`} className="rounded-xl border shadow-sm bg-card">
              <CardContent className="p-4">
                <s.icon className={`w-4 h-4 ${s.color} mb-2`} strokeWidth={1.5} />
                <p className="text-2xl font-bold font-heading text-foreground">{s.value}</p>
                <p className="text-[10px] text-muted-foreground">{s.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Bledy wg typu */}
      {stats && (
        <div className="grid grid-cols-4 lg:grid-cols-8 gap-2">
          {Object.entries(typeConfig).map(([key, cfg]) => (
            <button key={key} data-testid={`type-filter-${key}`}
              onClick={() => setFilterType(filterType === key ? "all" : key)}
              className={`p-2.5 rounded-lg border text-center transition-all ${
                filterType === key ? "border-primary bg-primary/5 shadow-sm" : "border-border hover:border-primary/30"
              }`}>
              <cfg.icon className={`w-4 h-4 mx-auto mb-1 ${filterType === key ? "text-primary" : "text-muted-foreground"}`} strokeWidth={1.5} />
              <p className="text-[10px] font-medium text-foreground">{cfg.label}</p>
              <p className="text-xs font-bold text-foreground">{stats.by_type[key] || 0}</p>
            </button>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Lista alertow */}
        <div className={selectedAlert ? "lg:col-span-1" : "lg:col-span-3"}>
          <Card className="rounded-xl border shadow-sm bg-card">
            <CardHeader className="pb-2 pt-4 px-5">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <CardTitle className="text-sm font-heading flex items-center gap-2 text-foreground">
                  <Shield className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
                  {t("monitor.alertsList")}
                  <Badge variant="secondary" className="text-[10px] rounded-md">{alerts.length}</Badge>
                </CardTitle>
                <div className="flex gap-2">
                  <Select value={filterSeverity} onValueChange={setFilterSeverity}>
                    <SelectTrigger data-testid="severity-filter" className="h-7 text-[10px] w-[100px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all" className="text-xs">{t("monitor.allSeverity")}</SelectItem>
                      <SelectItem value="critical" className="text-xs">Critical</SelectItem>
                      <SelectItem value="high" className="text-xs">High</SelectItem>
                      <SelectItem value="medium" className="text-xs">Medium</SelectItem>
                      <SelectItem value="low" className="text-xs">Low</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger data-testid="status-filter" className="h-7 text-[10px] w-[100px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all" className="text-xs">{t("monitor.allStatus")}</SelectItem>
                      <SelectItem value="new" className="text-xs">{t("monitor.statusNew")}</SelectItem>
                      <SelectItem value="acknowledged" className="text-xs">{t("monitor.statusAck")}</SelectItem>
                      <SelectItem value="resolved" className="text-xs">{t("monitor.statusResolved")}</SelectItem>
                      <SelectItem value="ignored" className="text-xs">{t("monitor.statusIgnored")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent className="px-5 pb-4 pt-1">
              {alerts.length === 0 ? (
                <div data-testid="no-alerts" className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                  <CheckCircle2 className="w-10 h-10 mb-3 opacity-20 text-emerald-500" strokeWidth={1} />
                  <p className="text-xs">{t("monitor.noAlerts")}</p>
                </div>
              ) : (
                <>
                {/* Bulk-action toolbar */}
                <div className="flex items-center flex-wrap gap-2 mb-2 px-2 py-2 bg-slate-50 rounded-lg border border-slate-100">
                  <input
                    type="checkbox"
                    checked={selectedIds.size > 0 && selectedIds.size === alerts.filter(a => a.status === "new").length}
                    onChange={(e) => e.target.checked ? selectAllVisible() : clearSelection()}
                    title="Zaznacz wszystkie nowe"
                    className="w-3.5 h-3.5"
                  />
                  <span className="text-xs text-slate-600">
                    Zaznaczone: <strong>{selectedIds.size}</strong>
                  </span>
                  {selectedIds.size > 0 && (
                    <>
                      <Button size="sm" variant="outline" className="h-7 text-xs"
                        disabled={bulkProcessing}
                        onClick={() => handleBulkResolve("resolved")}>
                        Resolve {selectedIds.size}
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 text-xs"
                        disabled={bulkProcessing}
                        onClick={() => handleBulkResolve("acknowledged")}>
                        Acknowledge
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 text-xs"
                        disabled={bulkProcessing}
                        onClick={() => handleBulkResolve("ignored")}>
                        Ignore
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 text-xs"
                        onClick={clearSelection}>
                        Anuluj
                      </Button>
                    </>
                  )}
                  <div className="ml-auto">
                    <Button size="sm" variant="outline" className="h-7 text-xs"
                      disabled={bulkProcessing}
                      onClick={handleBulkByFilter}
                      title="Resolve starszych niż X godzin (z bieżącymi filtrami)">
                      <Clock className="w-3 h-3 mr-1" /> Resolve stare
                    </Button>
                  </div>
                </div>

                <ScrollArea className="max-h-[500px]">
                  <div className="space-y-1.5">
                    {alerts.map((alert) => {
                      const sev = severityConfig[alert.severity] || severityConfig.medium;
                      const typ = typeConfig[alert.error_type] || typeConfig.system;
                      const SevIcon = sev.icon;
                      const TypeIcon = typ.icon;
                      const isSelected = selectedIds.has(alert.id);
                      return (
                        <div key={alert.id} data-testid={`alert-${alert.id}`}
                          className={`w-full flex items-start gap-2 p-3 rounded-lg border text-left transition-all cursor-pointer ${
                            isSelected
                              ? "border-blue-400 bg-blue-50/50"
                              : selectedAlert?.id === alert.id
                              ? `${sev.border} bg-card shadow-sm`
                              : "border-border hover:border-primary/20 hover:bg-accent/30"
                          }`}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={(e) => { e.stopPropagation(); toggleSelect(alert.id); }}
                            onClick={(e) => e.stopPropagation()}
                            className="w-3.5 h-3.5 mt-1 shrink-0"
                          />
                          <div className="flex-1 min-w-0 flex items-start gap-3"
                            onClick={() => setSelectedAlert(alert)}>
                            <SevIcon className={`w-4 h-4 mt-0.5 shrink-0 ${sev.color}`} strokeWidth={1.5} />
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium text-foreground truncate">{alert.message}</p>
                              <div className="flex items-center gap-1.5 mt-1">
                                <Badge className={`text-[9px] border-0 rounded-md px-1.5 ${sev.bg}`}>{alert.severity}</Badge>
                                <Badge variant="outline" className="text-[9px] rounded-md px-1.5 gap-0.5">
                                  <TypeIcon className="w-2.5 h-2.5" />{typ.label}
                                </Badge>
                                <Badge className={`text-[9px] border-0 rounded-md px-1.5 ${statusConfig[alert.status] || statusConfig.new}`}>{alert.status}</Badge>
                              </div>
                            </div>
                            <span className="text-[9px] text-muted-foreground shrink-0 mt-0.5">
                              {new Date(alert.created_at).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Detail panel */}
        {selectedAlert && (
          <div className="lg:col-span-2">
            <AlertDetail
              alert={selectedAlert}
              onAnalyze={handleAnalyze}
              onStatusChange={handleStatusChange}
              onDelete={handleDelete}
              analyzingId={analyzingId}
              t={t}
              onRefresh={() => {
                fetchData().then(() => {
                  // Re-select updated alert
                  setAlerts(prev => {
                    const upd = prev.find(a => a.id === selectedAlert.id);
                    if (upd) setSelectedAlert(upd);
                    return prev;
                  });
                });
              }}
            />
          </div>
        )}
      </div>

      {/* Czas ostatniego skanu */}
      {stats?.last_scan && (
        <p className="text-[10px] text-muted-foreground/50 flex items-center gap-1">
          <Clock className="w-3 h-3" /> {t("monitor.lastScan")}: {new Date(stats.last_scan).toLocaleString()}
        </p>
      )}
    </div>
  );
}

function AlertDetail({ alert, onAnalyze, onStatusChange, onDelete, analyzingId, t, onRefresh }) {
  const sev = severityConfig[alert.severity] || severityConfig.medium;
  const typ = typeConfig[alert.error_type] || typeConfig.system;
  const SevIcon = sev.icon;

  return (
    <Card data-testid="alert-detail" className="rounded-xl border shadow-sm bg-card">
      <CardHeader className="pb-2 pt-4 px-5">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <SevIcon className={`w-5 h-5 mt-0.5 ${sev.color}`} strokeWidth={1.5} />
            <div>
              <CardTitle className="text-sm font-heading text-foreground">{alert.message}</CardTitle>
              <p className="text-[11px] text-muted-foreground mt-0.5">{alert.source} — {new Date(alert.created_at).toLocaleString()}</p>
            </div>
          </div>
          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => onDelete(alert.id)}>
            <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="px-5 pb-5 pt-2 space-y-4">
        {/* Badges */}
        <div className="flex items-center gap-2 flex-wrap">
          <Badge className={`text-[10px] border-0 rounded-md ${sev.bg}`}>{alert.severity}</Badge>
          <Badge variant="outline" className="text-[10px] rounded-md">{typ.label}</Badge>
          <Badge className={`text-[10px] border-0 rounded-md ${statusConfig[alert.status]}`}>{alert.status}</Badge>
        </div>

        {/* Szczegoly */}
        {alert.details && Object.keys(alert.details).length > 0 && (
          <div className="p-3 rounded-lg bg-muted/50">
            <p className="text-[10px] uppercase font-semibold text-muted-foreground tracking-wider mb-1.5">{t("monitor.details")}</p>
            {Object.entries(alert.details).map(([k, v]) => (
              <div key={k} className="flex items-start gap-2 text-xs py-0.5">
                <span className="font-medium text-muted-foreground min-w-[80px]">{k}:</span>
                <span className="text-foreground break-all">{String(v)}</span>
              </div>
            ))}
          </div>
        )}

        {/* AI Analiza */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold text-foreground">{t("monitor.aiAnalysis")}</p>
            <Button size="sm" data-testid="analyze-alert-btn"
              onClick={() => onAnalyze(alert.id)}
              disabled={analyzingId === alert.id}
              className="h-7 text-xs gap-1.5 rounded-md">
              {analyzingId === alert.id ? <Loader2 className="w-3 h-3 animate-spin" /> :
               <Sparkles className="w-3 h-3" strokeWidth={1.5} />}
              {analyzingId === alert.id ? t("monitor.analyzing") : t("monitor.analyzeError")}
            </Button>
          </div>

          {alert.ai_analysis && (
            <div className="space-y-2">
              <div className="p-3 rounded-lg bg-muted/50">
                <p className="text-[10px] uppercase font-semibold text-muted-foreground tracking-wider mb-1">{t("monitor.cause")}</p>
                <p className="text-xs text-foreground">{alert.ai_analysis.cause}</p>
              </div>
              {alert.ai_analysis.solution && (
                <div className="p-3 rounded-lg bg-muted/50">
                  <p className="text-[10px] uppercase font-semibold text-muted-foreground tracking-wider mb-1">{t("monitor.solution")}</p>
                  {(Array.isArray(alert.ai_analysis.solution) ? alert.ai_analysis.solution : [alert.ai_analysis.solution]).map((s, i) => (
                    <p key={i} className="text-xs text-foreground py-0.5 pl-3 border-l-2 border-primary/40">{s}</p>
                  ))}
                </div>
              )}
              <div className="flex items-center gap-3">
                {alert.ai_analysis.urgent && (
                  <Badge className="bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 text-[10px] border-0 rounded-md">
                    {t("monitor.urgent")}
                  </Badge>
                )}
                {alert.ai_analysis.impact && (
                  <p className="text-[10px] text-muted-foreground">{t("monitor.impact")}: {alert.ai_analysis.impact}</p>
                )}
              </div>
            </div>
          )}
        </div>

        <Separator />

        {/* Akcje statusu */}
        <div className="flex items-center gap-2 flex-wrap">
          {alert.status === "new" && (
            <Button variant="outline" size="sm" className="h-7 text-xs rounded-md"
              data-testid="ack-alert-btn"
              onClick={() => onStatusChange(alert.id, "acknowledged")}>
              {t("monitor.acknowledge")}
            </Button>
          )}
          {alert.status !== "resolved" && (
            <Button variant="outline" size="sm" className="h-7 text-xs rounded-md text-emerald-600"
              data-testid="resolve-alert-btn"
              onClick={() => onStatusChange(alert.id, "resolved")}>
              <CheckCircle2 className="w-3 h-3 mr-1" /> {t("monitor.resolve")}
            </Button>
          )}
          {alert.status !== "ignored" && alert.status !== "resolved" && (
            <Button variant="ghost" size="sm" className="h-7 text-xs rounded-md text-muted-foreground"
              onClick={() => onStatusChange(alert.id, "ignored")}>
              {t("monitor.ignore")}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
